import * as THREE from "three";
import type { UVTransform } from "@visorxr/core";
import { projectiveUvWeights, quadToClipSpace } from "./homography-quad";
import type { FrameSize, Quad } from "./homography-quad";
import type { ARScene } from "./scene";

const VERTEX_SHADER = /* glsl */ `
  attribute vec3 uvq;
  varying vec3 vUvq;
  void main() {
    vUvq = uvq;
    // Corners arrive already in clip space: the overlay is a 2D warp of the camera image, not an
    // object sitting in the 3D scene, so no camera or model matrix takes part.
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  // three prefixes highp; overriding to mediump would interpolate UVs at fp16 on many mobile GPUs,
  // which is sampling wobble on the one thing this project is trying to hold sub-pixel steady.
  uniform sampler2D map;
  uniform float opacity;
  varying vec3 vUvq;
  void main() {
    // The perspective divide the GPU's affine interpolation cannot do for us.
    vec2 uv = vUvq.xy / vUvq.z;
    // Sampled alpha is preserved, not replaced: an opaque video reads 1 here so the confidence
    // fade is unchanged, while a transparent PNG/WebP layer would otherwise render its empty
    // regions as an opaque matte rectangle over whatever it sits on.
    vec4 texel = texture2D(map, uv);
    gl_FragColor = vec4(texel.rgb, texel.a * opacity);
  }
`;

const DEFAULT_RENDER_ORDER = 10;

export interface HomographySurfaceOptions {
  /** `THREE.VideoTexture` (auto-refreshes every frame) vs a static `THREE.Texture` (uploaded once,
   *  on construction). Defaults to "video" — every call site before positioned layers existed. */
  kind?: "video" | "image";
  /** Paint order among stacked overlay meshes; higher paints later (on top). Defaults to the value
   *  every surface used before layers existed. A base video plus positioned layers should give
   *  each an increasing value so array order becomes paint order. */
  renderOrder?: number;
  /** Told once, when `ensureVideoTexturePath` settles which route delivers this video's pixels. */
  onVideoTexturePath?: (path: VideoTexturePath) => void;
  /**
   * Sub-rectangle of the texture the quad samples, as a centred repeat/offset — what
   * `videoFit: "cover"` needs so an aspect-mismatched video is cropped to the marker rather than
   * stretched across it. Defaults to the whole texture.
   */
  uvTransform?: UVTransform;
}

/**
 * How a video's pixels reach its overlay: `direct` is the ordinary VideoTexture upload, `relay`
 * routes each frame through a 2D canvas because the direct upload yields nothing (a WKWebView
 * embedding failure — Chrome on iOS — where a file-backed video decodes and plays audio while
 * `texImage2D` from it stays empty), and `dead` is the canvas machinery itself failing (no 2D
 * context, or a throwing draw) — an operational verdict, never a pixel-value one, reported so
 * the field can see it rather than inferring it from silence.
 */
export type VideoTexturePath = "direct" | "relay" | "dead";

/** Mismatch frames — the texture reading black while a 2D draw of the same source shows color —
 *  required before the direct route is condemned; spread over display frames so a transient
 *  upload hiccup cannot convict on its own. */
const TEXTURE_PROBE_ATTEMPTS = 5;

/** Long edge the relay canvas is capped to — see the relay engagement for why. */
const RELAY_MAX_DIMENSION_PX = 1024;

/** Widest stride, in presented frames, between probes while the evidence stays ambiguous —
 *  roughly one probe per second of dark playback, against unbounded readbacks without it. */
const AMBIGUOUS_PROBE_BACKOFF_MAX = 30;

/** Severity order for reconciling several surfaces' decisions into one session-level reading. */
export function worstVideoTexturePath(a: VideoTexturePath | null, b: VideoTexturePath): VideoTexturePath {
  const rank = { direct: 0, relay: 1, dead: 2 } as const;
  return a !== null && rank[a] >= rank[b] ? a : b;
}

/**
 * Draws a video or image warped by a tracked homography over the camera background — L0 (docs/10).
 * One base overlay (the marker's full-rect video) plus any number of positioned layers all use
 * this same class; only the texture source and paint order differ between them.
 *
 * The overlay is a screen-space projective warp, never a 3D object: the tracker's output is a
 * homography, and decomposing it into a pose to place a plane would reintroduce exactly the
 * ill-conditioned step the architecture exists to avoid.
 *
 * The quad is a mesh inside `ARScene.scene` rather than a separate render pass, because both
 * `render()` and `renderForCapture()` issue a single draw of that scene — so photo capture picks
 * the overlay up with no changes to the capture path at all.
 *
 * The background crop (`ARScene.getBackgroundCoverFit`) is viewport state, not pose state: it is
 * folded in on every `setQuad`, and re-applied to the last quad before a render whenever it has
 * changed. Waiting for the next pose would not do — a limited-state overlay deliberately holds its
 * last-good quad for the whole recovery window, so a rotation mid-recovery would leave it
 * registered against a background that has already re-cropped underneath it.
 */
/** No crop — the whole texture is stretched across the tracked quad (`videoFit: "exact"`). */
const WHOLE_TEXTURE: UVTransform = { repeat: { x: 1, y: 1 }, offset: { x: 0, y: 0 } };

export class HomographySurface {
  private readonly uvTransform: UVTransform;
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly texture: THREE.Texture;
  private readonly positions: Float32Array;
  private readonly uvq: Float32Array;
  // The pose the geometry currently encodes, kept so a viewport change can be re-applied to it.
  private lastQuad: Quad | null = null;
  private lastFrame: FrameSize | null = null;
  private appliedCoverFit: UVTransform | null = null;
  private videoPath: VideoTexturePath | null;
  private directMismatchReads = 0;
  // Probe pacing: only a newly presented frame can carry new evidence, and ambiguous frames
  // (both routes black, or no reference) stretch the stride so a dark or paused stretch stops
  // taxing the tracking loop with synchronous readbacks. Frame identity comes from the frame
  // clock (rVFC) where it exists, else the decode counter; the media clock is the last resort —
  // it advances with playbackRate whether or not a new frame was presented, and re-counting one
  // frame per tick would collapse the mismatch grace into a single frame's evidence.
  private probeWatchStarted = false;
  private probeUsesFrameClock = false;
  private probeFrameDirty = true;
  private probeFrameRequest = 0;
  private probeVideo: HTMLVideoElement | null = null;
  private lastProbeToken = -1;
  private probeBackoff = 1;
  private skipProbes = 0;
  // The source-reference control's 2D context: undefined until first needed, null when creation
  // failed — in which case no transport evidence can be gathered and the path stays undecided.
  private referenceContext: CanvasRenderingContext2D | null | undefined;
  private relayCanvas: HTMLCanvasElement | null = null;
  private relayContext: CanvasRenderingContext2D | null = null;
  private relayTexture: THREE.CanvasTexture | null = null;
  // One canvas upload per presented video frame, not per render: the frame clock (rVFC) marks
  // arrivals where the browser exposes it, and display rate can be several times the source fps
  // on exactly the hardware that needed the fallback.
  private relayFrameDirty = false;
  private relayUsesFrameClock = false;
  private relayFrameRequest = 0;
  private relayVideo: HTMLVideoElement | null = null;
  private readonly onVideoTexturePath: ((path: VideoTexturePath) => void) | undefined;

  constructor(
    private readonly scene: ARScene,
    source: HTMLVideoElement | HTMLImageElement,
    options: HomographySurfaceOptions = {},
  ) {
    this.uvTransform = options.uvTransform ?? WHOLE_TEXTURE;
    // Images upload once at construction and either work or fail loudly; only video needs a path
    // decided, so an image surface is born decided.
    this.videoPath = options.kind === "image" ? "direct" : null;
    this.onVideoTexturePath = options.onVideoTexturePath;
    if (options.kind === "image") {
      this.texture = new THREE.Texture(source as HTMLImageElement);
      // A static image's pixels never change on their own, unlike VideoTexture (which refreshes
      // itself every frame) — without this the GPU never uploads them and the layer renders blank.
      this.texture.needsUpdate = true;
    } else {
      this.texture = new THREE.VideoTexture(source as HTMLVideoElement);
    }
    this.texture.colorSpace = THREE.SRGBColorSpace;

    this.material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: { map: { value: this.texture }, opacity: { value: 1 } },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    // Two triangles over the tracked corners: TL, TR, BR, BL.
    this.positions = new Float32Array(12);
    this.uvq = new Float32Array(12);
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute("uvq", new THREE.BufferAttribute(this.uvq, 3));
    this.geometry.setIndex([0, 2, 1, 0, 3, 2]);

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    // The vertices are already in clip space, so frustum culling would test the wrong volume and
    // could drop the overlay entirely.
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = options.renderOrder ?? DEFAULT_RENDER_ORDER;
    this.mesh.visible = false;
    // Catches a viewport change the tracker has no reason to report — the crop must be current at
    // draw time, not at the time of the last pose. The relay pump lives here too: a canvas-routed
    // video has its pixels copied at draw time, paced by the element's frame clock where one exists.
    this.mesh.onBeforeRender = (): void => {
      this.refitToBackground();
      this.pumpRelay(source);
    };
    this.scene.scene.add(this.mesh);
  }

  /**
   * Settle which route delivers this video's pixels, one probe per call. Call it once per applied
   * frame while the overlay is on screen; it no-ops for images, before the video has a frame to
   * read (`readyState` below HAVE_CURRENT_DATA), and once decided. The direct route gets
   * abandoned only on repeated *mismatch* — the texture reading black on a frame a 2D draw of
   * the same source shows in color, the WKWebView failure's own signature — never on black
   * readbacks alone, which are just as likely dark content (a fade-from-black intro outlives any
   * probe budget, and a content-based fallback would misreport healthy sessions as `relay`).
   * While source and texture agree on black there is no transport evidence and nothing is
   * counted; an all-black video therefore keeps probing, which is the honest cost of an asset
   * that never shows a distinguishing frame. The relay engages on its first successful draw, and
   * dead means the relay's canvas machinery itself failed; either way the direct texture stays
   * bound when there is nothing better to show. Probes are paced: only a newly presented frame
   * is probed at all — a paused frame cannot change its evidence, and the mismatch grace counts
   * distinct frames, not tracker ticks — and ambiguous frames back the stride off exponentially,
   * so an all-black stretch costs about one readback a second rather than two per tracked frame
   * per surface.
   */
  ensureVideoTexturePath(): void {
    if (this.videoPath !== null) return;
    const video = this.texture.image as HTMLVideoElement;
    if (!video || video.readyState < 2) return;
    if (!this.shouldProbe(video)) return;

    const pixels = this.scene.readTexturePixels(this.texture);
    // No pixels is no evidence — the probe machinery itself could not run (its own control
    // texture read black), and counting it would convict the upload for the probe's failure.
    if (!pixels) return;
    if (anyColor(pixels)) {
      this.decideVideoPath("direct");
      return;
    }
    // Any frame the upload does show color decides direct above before mismatches could accrue
    // falsely, so this counter only ever fills on a genuinely silent texture.
    const reference = this.sourceReferencePixels(video);
    if (!reference || !anyColor(reference)) {
      // Ambiguity teaches nothing; stretch the interval until the source shows something.
      this.probeBackoff = Math.min(this.probeBackoff * 2, AMBIGUOUS_PROBE_BACKOFF_MAX);
      this.skipProbes = this.probeBackoff - 1;
      return;
    }
    // A visible source is evidence flowing again: probe consecutively, and let any later dark
    // stretch restart its backoff from the bottom rather than inherit the opening's stride.
    this.probeBackoff = 1;
    if (++this.directMismatchReads < TEXTURE_PROBE_ATTEMPTS) return;

    // Capped: the relay re-uploads the whole canvas per drawn frame, and a full 1080p copy is
    // megabytes per upload on exactly the hardware that needed the fallback. The overlay warps
    // the texture anyway, so a bounded canvas costs resolution, not correctness.
    const scale = Math.min(1, RELAY_MAX_DIMENSION_PX / Math.max(video.videoWidth || 1, video.videoHeight || 1));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(2, Math.round((video.videoWidth || 2) * scale));
    canvas.height = Math.max(2, Math.round((video.videoHeight || 2) * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      this.decideVideoPath("dead");
      return;
    }
    try {
      // readyState ≥ 2 rules out the spec's InvalidStateError, so a throw here is the
      // implementation itself refusing video rasterization — the relay genuinely cannot run.
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch {
      this.decideVideoPath("dead");
      return;
    }
    this.relayCanvas = canvas;
    this.relayContext = context;
    this.relayTexture = new THREE.CanvasTexture(canvas);
    this.relayTexture.colorSpace = THREE.SRGBColorSpace;
    this.material.uniforms.map.value = this.relayTexture;
    this.watchRelayFrames(video);
    this.decideVideoPath("relay");
  }

  /** One probe per presented frame, minus the ambiguity backoff's skipped frames. */
  private shouldProbe(video: HTMLVideoElement): boolean {
    this.armProbeFrames(video);
    if (this.probeUsesFrameClock) {
      if (!this.probeFrameDirty) return false;
      this.probeFrameDirty = false;
    } else {
      const token = video.getVideoPlaybackQuality?.().totalVideoFrames ?? video.currentTime;
      if (token === this.lastProbeToken) return false;
      this.lastProbeToken = token;
    }
    if (this.skipProbes > 0) {
      this.skipProbes--;
      return false;
    }
    return true;
  }

  /** Frame identity for probing, armed once. `probeFrameDirty` starts true: by the time probing
   *  is legal (`readyState` ≥ 2) a frame is already presented and deserves its one probe. */
  private armProbeFrames(video: HTMLVideoElement): void {
    if (this.probeWatchStarted) return;
    this.probeWatchStarted = true;
    if (typeof video.requestVideoFrameCallback !== "function") return;
    this.probeUsesFrameClock = true;
    this.probeVideo = video;
    const onFrame = (): void => {
      this.probeFrameDirty = true;
      this.probeFrameRequest = video.requestVideoFrameCallback(onFrame);
    };
    this.probeFrameRequest = video.requestVideoFrameCallback(onFrame);
  }

  private teardownProbeWatch(): void {
    if (this.probeVideo && this.probeFrameRequest !== 0) {
      this.probeVideo.cancelVideoFrameCallback(this.probeFrameRequest);
    }
    this.probeVideo = null;
    this.probeFrameRequest = 0;
    this.probeUsesFrameClock = false;
  }

  private decideVideoPath(path: VideoTexturePath): void {
    this.videoPath = path;
    this.referenceContext = undefined;
    this.teardownProbeWatch();
    this.onVideoTexturePath?.(path);
  }

  /** The same frame through a 2D canvas, as a 2×2 downsample: color here while the texture reads
   *  black is the transport mismatch; null is machinery failure, which is evidence of nothing. */
  private sourceReferencePixels(video: HTMLVideoElement): Uint8ClampedArray | null {
    if (this.referenceContext === undefined) {
      const canvas = document.createElement("canvas");
      canvas.width = 2;
      canvas.height = 2;
      this.referenceContext = canvas.getContext("2d");
    }
    if (!this.referenceContext) return null;
    try {
      this.referenceContext.drawImage(video, 0, 0, 2, 2);
      return this.referenceContext.getImageData(0, 0, 2, 2).data;
    } catch {
      return null;
    }
  }

  /** Marks a pending upload whenever the element presents a new frame. Browsers without the frame
   *  clock get no watch at all — the pump then copies every render, the only correct fallback. */
  private watchRelayFrames(video: HTMLVideoElement): void {
    if (typeof video.requestVideoFrameCallback !== "function") return;
    this.relayUsesFrameClock = true;
    this.relayVideo = video;
    const onFrame = (): void => {
      this.relayFrameDirty = true;
      this.relayFrameRequest = video.requestVideoFrameCallback(onFrame);
    };
    this.relayFrameRequest = video.requestVideoFrameCallback(onFrame);
  }

  private pumpRelay(source: HTMLVideoElement | HTMLImageElement): void {
    if (!this.relayTexture || !this.relayContext || !this.relayCanvas) return;
    const video = source as HTMLVideoElement;
    // Unpaused does not mean drawable: a stall heal calls load() and the next playback sync calls
    // play() before the element holds a frame again — a draw there throws. This runs inside
    // onBeforeRender, and an exception escaping it stops setAnimationLoop for the whole session.
    if (video.paused || video.readyState < 2) return;
    if (this.relayUsesFrameClock && !this.relayFrameDirty) return;
    this.relayFrameDirty = false;

    try {
      this.relayContext.drawImage(video, 0, 0, this.relayCanvas.width, this.relayCanvas.height);
      this.relayTexture.needsUpdate = true;
    } catch {
      // The transport worked and then stopped. Reported dead rather than crashing the loop; the
      // direct texture returns as the nothing-better-to-show choice.
      this.teardownRelay();
      this.decideVideoPath("dead");
    }
  }

  private teardownRelay(): void {
    if (this.relayVideo && this.relayFrameRequest !== 0) {
      this.relayVideo.cancelVideoFrameCallback(this.relayFrameRequest);
    }
    this.relayVideo = null;
    this.relayFrameRequest = 0;
    this.relayUsesFrameClock = false;
    this.relayFrameDirty = false;
    this.relayTexture?.dispose();
    this.relayTexture = null;
    this.relayCanvas = null;
    this.relayContext = null;
    this.material.uniforms.map.value = this.texture;
  }

  /** Place the overlay on the tracked corners, in the pixel space the tracker ran at. */
  setQuad(quad: Quad, frame: FrameSize): void {
    this.lastQuad = quad;
    this.lastFrame = { width: frame.width, height: frame.height };
    this.writeGeometry(quad, this.lastFrame, this.scene.getBackgroundCoverFit());
  }

  /** Re-apply the current background crop to the last pose, when (and only when) it has moved. */
  private refitToBackground(): void {
    if (!this.lastQuad || !this.lastFrame) return;
    const coverFit = this.scene.getBackgroundCoverFit();
    if (sameCoverFit(coverFit, this.appliedCoverFit)) return;
    this.writeGeometry(this.lastQuad, this.lastFrame, coverFit);
  }

  private writeGeometry(quad: Quad, frame: FrameSize, coverFit: UVTransform | null): void {
    const clip = quadToClipSpace(quad, frame, coverFit ?? undefined);
    const weights = projectiveUvWeights(quad);
    const corners: [number, number][] = [
      [0, 1],
      [1, 1],
      [1, 0],
      [0, 0],
    ];

    for (let i = 0; i < 4; i++) {
      this.positions[i * 3] = clip[i].x;
      this.positions[i * 3 + 1] = clip[i].y;
      this.positions[i * 3 + 2] = 0;

      // UVs pre-multiplied by the corner weight; the fragment shader divides them back out.
      const weight = weights[i];
      const u = this.uvTransform.offset.x + corners[i][0] * this.uvTransform.repeat.x;
      const v = this.uvTransform.offset.y + corners[i][1] * this.uvTransform.repeat.y;
      this.uvq[i * 3] = u * weight;
      this.uvq[i * 3 + 1] = v * weight;
      this.uvq[i * 3 + 2] = weight;
    }

    this.geometry.getAttribute("position").needsUpdate = true;
    this.geometry.getAttribute("uvq").needsUpdate = true;
    this.appliedCoverFit = coverFit;
  }

  /** Confidence fade: a frozen, dimmed overlay reads as "hold on"; a misplaced one reads as broken. */
  setOpacity(alpha: number): void {
    // Visibility follows opacity alone, so writing geometry can never un-hide a faded-out overlay.
    this.material.uniforms.opacity.value = alpha;
    this.mesh.visible = alpha > 0;
  }

  hide(): void {
    this.mesh.visible = false;
  }

  /**
   * Hide and forget where this surface last drew. `hide()` alone leaves the geometry in place, so a
   * surface that is shown again — a marker-library poster the user turns back to — reappears at the
   * pose from its previous activation on the first frame that raises opacity without writing a
   * fresh quad.
   */
  clearQuad(): void {
    this.lastQuad = null;
    this.lastFrame = null;
    this.positions.fill(0);
    this.geometry.getAttribute("position").needsUpdate = true;
    this.hide();
  }

  dispose(): void {
    this.scene.scene.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
    this.texture.dispose();
    this.teardownProbeWatch();
    this.teardownRelay();
  }
}

/** Alpha is excluded on purpose: three's not-yet-uploaded placeholder samples transparent black,
 *  but an allocated-and-empty texture on a failing upload path may well sample opaque — alpha can
 *  prove allocation, never content. Color proves content, and the probe's raw shader reads
 *  unconverted bytes, so a single sRGB step above black already counts. */
function anyColor(rgba: ArrayLike<number>): boolean {
  for (let i = 0; i < rgba.length; i += 4) {
    if (rgba[i] > 0 || rgba[i + 1] > 0 || rgba[i + 2] > 0) return true;
  }
  return false;
}

function sameCoverFit(a: UVTransform | null, b: UVTransform | null): boolean {
  if (a === null || b === null) return a === b;
  return a.repeat.x === b.repeat.x && a.repeat.y === b.repeat.y && a.offset.x === b.offset.x && a.offset.y === b.offset.y;
}
